import { useEffect, useState } from 'react'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { MoreVertical } from 'lucide-react'
import {
  useBookDelivery,
  useCancelDelivery,
  useDelivery,
} from '../hooks/use-deliveries'
import { useDeliveryRestaurantStore } from '../store/delivery-restaurant-store'
import { DELIVERY_STATUS_VARIANT } from '../lib/delivery-status-variant'
import { DeliveryCheckView } from '../components/delivery-check-view'
import { AffectedOrdersDialog } from '../components/affected-orders-dialog'
import {
  DeliveryCheckHeader,
  DeliveryCheckLineRow,
  GoodsValueFooter,
  LINES_CONTAINER,
} from '../components/delivery-line-editor'
import { formatIsoDate, formatTimestamp } from '../components/delivery-columns'
import {
  checkLinesFromDelivery,
  goodsValueCents,
  hasPrices,
  isApiClientActor,
} from '../lib/delivery-lines'
import type { CheckLine } from '../lib/delivery-lines'
import type {
  AffectedOrder,
  DeliveryDetail,
} from '@/api/endpoints/inventory-deliveries'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

const toCheckLines = (delivery: DeliveryDetail): Array<CheckLine> =>
  checkLinesFromDelivery(delivery.lines)

/**
 * A booked or cancelled document: the same position layout as the check view, read-only, with
 * the booked document's own goods value — so it does not change if the article's package data is
 * corrected later.
 */
function ReadOnlyLines({ delivery }: { delivery: DeliveryDetail }) {
  const { t } = useTranslation('inventory-deliveries')
  const lines = toCheckLines(delivery)
  const showPrices = hasPrices(lines)
  const goodsValue = goodsValueCents(lines)
  return (
    <>
      <div className={LINES_CONTAINER}>
        <DeliveryCheckHeader readOnly showPrices={showPrices} />
        {lines.map((line) => (
          <DeliveryCheckLineRow
            key={line.sku}
            line={line}
            readOnly
            showPrices={showPrices}
            cancelled={delivery.status === 'CANCELLED'}
          />
        ))}
      </div>
      {goodsValue != null && (
        <GoodsValueFooter label={t('detail.goodsValue')} cents={goodsValue} />
      )}
    </>
  )
}

export function DeliveryDetailPage() {
  const { t } = useTranslation('inventory-deliveries')
  const { id } = useParams({ strict: false })
  const { restaurantId: linkedRestaurantId } = useSearch({ strict: false })
  const restaurantId = useDeliveryRestaurantStore((s) => s.restaurantId)
  const setRestaurantId = useDeliveryRestaurantStore((s) => s.setRestaurantId)

  // A deep link names its restaurant; adopt it so the fetch below and the list the user returns
  // to both show that location.
  useEffect(() => {
    if (linkedRestaurantId != null && linkedRestaurantId !== restaurantId) {
      setRestaurantId(linkedRestaurantId)
    }
  }, [linkedRestaurantId, restaurantId, setRestaurantId])
  const { can } = usePermissions()
  const canWrite = can(ACTIONS.INVENTORY_DELIVERY)

  const {
    data: delivery,
    isLoading,
    isError,
  } = useDelivery(restaurantId, id ?? null)
  const book = useBookDelivery(restaurantId, id ?? null)
  const cancel = useCancelDelivery(restaurantId, id ?? null)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [affected, setAffected] = useState<Array<AffectedOrder> | null>(null)

  const back = (
    <Button variant="outline" asChild>
      <Link to="/inventory/deliveries">{t('detail.back')}</Link>
    </Button>
  )

  return (
    <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
      <div className="flex flex-col gap-4">
        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {!isLoading && (isError || !delivery) && (
          <div className="flex flex-col items-start gap-3 py-8">
            <p className="text-sm text-muted-foreground">
              {t('detail.notFound')}
            </p>
            {back}
          </div>
        )}

        {delivery && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold">{t('detail.title')}</h1>
                <Badge variant={DELIVERY_STATUS_VARIANT[delivery.status]}>
                  {t(`status.${delivery.status}`)}
                </Badge>
                {delivery.overdue && (
                  <Badge variant="destructive">{t('status.overdue')}</Badge>
                )}
                {/* Where the document came from. A hand-announced delivery carries no badge —
                    that is the norm, and a badge on every row would say nothing. */}
                {delivery.source !== 'MANUAL' && (
                  <Badge variant="outline">
                    {t(`source.${delivery.source}`, {
                      defaultValue: delivery.source,
                    })}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canWrite && delivery.status === 'ANNOUNCED' && (
                  <>
                    <Button variant="outline" asChild>
                      <Link
                        to="/inventory/deliveries/$id/edit"
                        params={{ id: delivery.id }}
                      >
                        {t('detail.edit')}
                      </Link>
                    </Button>
                    {/* Cancelling is rare and destructive: one step away in the menu, so the
                        header reads calm and the red never sits next to the primary actions. */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label={t('detail.moreActions')}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => setCancelOpen(true)}
                        >
                          {t('detail.cancel')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
                {back}
              </div>
            </div>

            {/* The document head as a quiet grid: label above value, one glance per fact. */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 rounded-lg border bg-muted/30 px-4 py-3 sm:grid-cols-4">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('form.supplier')}
                </p>
                <p className="text-sm font-medium">
                  {delivery.supplier ?? '–'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('form.deliveryNoteRef')}
                </p>
                <p className="font-mono text-sm">
                  {delivery.deliveryNoteRef ?? '–'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('form.expectedDate')}
                </p>
                <p className="text-sm tabular-nums">
                  {formatIsoDate(delivery.expectedDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('form.countsForPreorders')}
                </p>
                <p className="text-sm">
                  {delivery.countsForPreorders
                    ? t('detail.yes')
                    : t('detail.no')}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
              <p>
                {isApiClientActor(delivery.createdBy)
                  ? t('detail.importedBy', {
                      client:
                        delivery.createdByClientName ?? delivery.createdBy,
                      date: formatTimestamp(delivery.createdAt),
                    })
                  : t('detail.announcedBy', {
                      actor: delivery.createdBy ?? '–',
                      date: formatTimestamp(delivery.createdAt),
                    })}
              </p>
              {delivery.status === 'BOOKED' && (
                <p>
                  {t('detail.bookedBy', {
                    actor: delivery.bookedBy ?? '–',
                    date: formatTimestamp(delivery.bookedAt),
                  })}
                </p>
              )}
              {delivery.status === 'CANCELLED' && (
                <p>
                  {t('detail.cancelledBy', {
                    actor: delivery.cancelledBy ?? '–',
                    date: formatTimestamp(delivery.cancelledAt),
                  })}
                </p>
              )}
              {delivery.overdue && (
                <p className="text-destructive">{t('detail.overdueHint')}</p>
              )}
              {delivery.purchaseOrderId && (
                <p>
                  <Link
                    to="/inventory/purchase-orders/$id"
                    params={{ id: delivery.purchaseOrderId }}
                    search={{ restaurantId: delivery.restaurantId }}
                    className="underline"
                  >
                    {`${t('detail.purchaseOrder')} →`}
                  </Link>
                </p>
              )}
            </div>

            {delivery.status === 'ANNOUNCED' ? (
              <DeliveryCheckView
                // Remounts when the document changes so the local received quantities are
                // rebuilt from the server document instead of surviving a booking.
                key={`${delivery.id}-${delivery.status}-${delivery.lines.length}`}
                initialLines={toCheckLines(delivery)}
                restaurantId={delivery.restaurantId}
                canWrite={canWrite}
                manualPrices={delivery.source === 'MANUAL'}
                booking={book.isPending}
                onBook={(lines) =>
                  book.mutate(
                    { lines },
                    {
                      onSuccess: (result) => {
                        toast.success(t('toast.booked'))
                        setAffected(result.affectedOrders)
                      },
                      onError: () => toast.error(t('toast.bookFailed')),
                    },
                  )
                }
              />
            ) : (
              <>
                <Card className="overflow-hidden py-0">
                  <CardContent className="p-0">
                    <ReadOnlyLines delivery={delivery} />
                  </CardContent>
                </Card>
                {delivery.status === 'BOOKED' && (
                  <div>
                    <Button variant="link" className="px-0" asChild>
                      <Link
                        to="/inventory/history"
                        search={{ reference: `delivery:${delivery.id}` }}
                      >
                        {`${t('detail.history')} →`}
                      </Link>
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('cancelDialog.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('cancelDialog.body')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancelDialog.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  cancel.mutate(undefined, {
                    onSuccess: (result) => {
                      toast.success(t('toast.cancelled'))
                      setAffected(result.affectedOrders)
                    },
                    onError: () => toast.error(t('toast.cancelFailed')),
                  })
                }
              >
                {t('cancelDialog.action')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AffectedOrdersDialog
          orders={affected}
          onClose={() => setAffected(null)}
        />
      </div>
    </PermissionGate>
  )
}
